<template>
  <v-container fluid class="dashboard-container">
    <template v-if="get_selected_company.company_name">

      <!-- Greeting -->
      <div class="dash-greeting mb-6">
        <h1 class="dash-greeting-title">{{ greeting }}, {{ firstName }}</h1>
        <div class="dash-greeting-sub">
          Here's what's happening at {{ get_selected_company.company_name }} today, {{ todayLabel }}.
        </div>
      </div>

      <!-- Stat tiles -->
      <v-row>
        <v-col v-for="tile in statTiles" :key="tile.key" cols="12" sm="6" lg="3">
          <router-link :to="tile.to" class="stat-tile-link">
            <v-card class="dash-card stat-tile pa-5">
              <div class="d-flex align-center justify-space-between">
                <span class="stat-label">{{ tile.label }}</span>
                <span class="stat-icon">
                  <v-icon size="18" color="#3988AA">{{ tile.icon }}</v-icon>
                </span>
              </div>
              <div class="stat-value mt-2">
                <span v-if="stats_loading" class="stat-value--loading">&ndash;</span>
                <span v-else>{{ tile.value }}</span>
              </div>
              <div class="d-flex align-center flex-wrap mt-2" style="gap:8px;">
                <span v-if="!stats_loading && tile.trend" class="stat-trend" :class="`stat-trend--${tile.trend.direction}`">
                  <v-icon v-if="tile.trend.direction !== 'flat'" size="14" class="stat-trend-icon">
                    {{ tile.trend.direction === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                  </v-icon>
                  {{ tile.trend.text }}
                </span>
                <span class="stat-sub">{{ stats_loading ? '' : tile.sub }}</span>
              </div>
            </v-card>
          </router-link>
        </v-col>
      </v-row>

      <v-row>
        <!-- Leads Per Day -->
        <v-col cols="12" md="8">
          <v-card class="dash-card fill-height">
            <div class="dash-card-header px-5 py-4 d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon color="white" size="18" class="mr-2">mdi-account-multiple-outline</v-icon>
                <span class="dash-card-title">Leads Per Day</span>
              </div>
              <v-btn small text dark class="dash-header-btn" :to="{ name: 'Leads' }">
                All Leads
                <v-icon right size="14">mdi-arrow-right</v-icon>
              </v-btn>
            </div>

            <v-card-text class="pa-4">
              <div v-if="leads_loading" class="dash-placeholder">
                <v-progress-circular indeterminate color="#3988AA" size="32" />
              </div>
              <div v-else-if="leads_chart_series[0].data.length === 0" class="dash-placeholder">
                <v-icon size="36" color="#D0D0D0">mdi-chart-line</v-icon>
                <div class="mt-2">No lead data available</div>
              </div>
              <apexchart v-else type="area" :options="leads_chart_options" :series="leads_chart_series" height="250" />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Needs attention -->
        <v-col cols="12" md="4">
          <v-card class="dash-card fill-height">
            <div class="dash-card-header px-5 py-4 d-flex align-center">
              <v-icon color="white" size="18" class="mr-2">mdi-bell-ring-outline</v-icon>
              <span class="dash-card-title">Needs Attention</span>
            </div>

            <div v-if="stats_loading" class="dash-placeholder">
              <v-progress-circular indeterminate color="#3988AA" size="32" />
            </div>
            <div v-else>
              <router-link v-for="item in attentionItems" :key="item.key" :to="item.to" class="attention-row">
                <span class="attention-icon" :class="{ 'attention-icon--clear': item.count === 0 }">
                  <v-icon size="18" :color="item.count === 0 ? '#9CC9A6' : item.color">{{ item.icon }}</v-icon>
                </span>
                <span class="attention-text">
                  <span class="attention-label">{{ item.label }}</span>
                  <span class="attention-hint">{{ item.hint }}</span>
                </span>
                <span class="attention-count" :class="{ 'attention-count--clear': item.count === 0 }">
                  <v-icon v-if="item.count === 0" size="16" color="#4CAF50">mdi-check</v-icon>
                  <template v-else>{{ item.count }}</template>
                </span>
              </router-link>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Upcoming Events -->
      <v-row>
        <v-col cols="12">
          <v-card class="dash-card">
            <div class="dash-card-header px-5 py-4 d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon color="white" size="18" class="mr-2">mdi-calendar-clock-outline</v-icon>
                <span class="dash-card-title">Upcoming Events</span>
              </div>
              <v-btn small text dark class="dash-header-btn" :to="{ name: 'BookedEvents' }">
                All Events
                <v-icon right size="14">mdi-arrow-right</v-icon>
              </v-btn>
            </div>

            <v-card-text class="pa-0">
              <div v-if="events_loading" class="dash-placeholder pa-6">
                <v-progress-circular indeterminate color="#3988AA" size="32" />
              </div>
              <div v-else-if="events.length === 0" class="dash-placeholder pa-6">
                <v-icon size="36" color="#D0D0D0">mdi-calendar-blank-outline</v-icon>
                <div class="mt-2">No upcoming events</div>
              </div>
              <div v-else class="events-list">
                <div v-for="(item, index) in events" :key="item.id" class="event-row"
                  :class="{ 'event-row--alt': index % 2 === 1 }">
                  <div class="event-when">
                    <div class="event-when-day">{{ $date(item.lead.start_date).format('DD') }}</div>
                    <div class="event-when-month">{{ $date(item.lead.start_date).format('MMM') }}</div>
                  </div>
                  <div class="event-row-main">
                    <router-link :to="{ name: 'ManageEvent', params: { event_id: item.id } }" class="event-name">
                      {{ item.name }}
                    </router-link>
                    <div class="event-meta mt-1">
                      <span class="event-meta-chip">
                        <v-icon size="12" color="#3988AA" class="mr-1">mdi-account-outline</v-icon>
                        {{ item.lead.first_name }} {{ item.lead.last_name }}
                      </span>
                      <span class="event-meta-chip" v-if="item.lead.location">
                        <v-icon size="12" color="#3988AA" class="mr-1">mdi-map-marker-outline</v-icon>
                        {{ item.lead.location.name }}
                      </span>
                      <span class="event-meta-chip" v-if="item.lead.people">
                        <v-icon size="12" color="#3988AA" class="mr-1">mdi-account-group-outline</v-icon>
                        {{ item.lead.people }} people
                      </span>
                    </div>
                  </div>
                  <div class="event-row-right">
                    <div class="event-countdown">{{ daysUntil(item.lead.start_date) }}</div>
                    <div class="event-code mt-1">{{ item.reference_code }}</div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

    </template>

    <CompanyPicker v-else />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CompanyPicker from './CompanyPicker.vue'

const UPCOMING_EVENTS_LIMIT = 8

export default {
  name: 'Dashboard',
  components: { CompanyPicker },
  data: () => ({
    now: new Date(),
    stats: null,
    stats_loading: true,
    events: [],
    events_loading: true,
    leads_loading: true,
    leads_chart_series: [{ name: 'Leads', data: [] }],
    leads_chart_options: {
      chart: {
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'Manrope, sans-serif',
      },
      dataLabels: { enabled: false },
      stroke: { colors: ['#3988AA'], width: 2, curve: 'smooth' },
      fill: {
        colors: ['#6ED4F4'],
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.55,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      xaxis: {
        categories: [],
        labels: { style: { colors: '#9E9E9E', fontSize: '11px' } },
      },
      yaxis: {
        labels: {
          style: { colors: '#9E9E9E', fontSize: '11px' },
          formatter: v => Math.round(v),
        },
        min: 0,
        tickAmount: 4,
      },
      grid: { borderColor: '#F0F0F0', strokeDashArray: 4 },
      tooltip: { theme: 'light' },
    },
  }),
  computed: {
    ...mapGetters({
      get_selected_company: 'auth/get_selected_company',
      get_user: 'auth/get_user',
    }),
    greeting() {
      const hour = this.now.getHours()
      if (hour < 12) return 'Good morning'
      if (hour < 18) return 'Good afternoon'
      return 'Good evening'
    },
    firstName() {
      return ((this.get_user && this.get_user.name) || '').trim().split(' ')[0] || 'there'
    },
    todayLabel() {
      return this.$date(this.now).format('dddd D MMMM')
    },
    statTiles() {
      const s = this.stats || {}
      const leads = s.leads || {}
      const quotes = s.quotes_sent || {}
      const events = s.new_events || {}
      const payments = s.payments || {}
      const conversion = s.conversion || {}
      const conversionRate = conversion.leads ? Math.round((conversion.converted / conversion.leads) * 100) : 0
      return [
        {
          key: 'leads',
          label: 'New leads this month',
          icon: 'mdi-account-plus-outline',
          value: this.formatNumber(leads.this_month),
          trend: this.trend(leads.this_month, leads.last_month_to_date),
          sub: `${this.formatNumber(leads.today)} today · ${this.formatNumber(leads.mine_this_month)} assigned to you`,
          to: { name: 'Leads' },
        },
        {
          key: 'quotes',
          label: 'Quotes sent this month',
          icon: 'mdi-email-fast-outline',
          value: this.formatNumber(quotes.this_month),
          trend: this.trend(quotes.this_month, quotes.last_month_to_date),
          sub: 'vs same point last month',
          to: { name: 'Leads' },
        },
        {
          key: 'events',
          label: 'New events this month',
          icon: 'mdi-calendar-star',
          value: this.formatNumber(events.this_month),
          trend: this.trend(events.this_month, events.last_month_to_date),
          sub: `${conversionRate}% of leads converted (${conversion.days || 90} days)`,
          to: { name: 'UnbookedEvents' },
        },
        {
          key: 'payments',
          label: 'Payments received this month',
          icon: 'mdi-cash-multiple',
          value: `${this.get_selected_company.currency || ''}${this.formatNumber(payments.this_month, 0)}`,
          trend: this.trend(payments.this_month, payments.last_month_to_date),
          sub: 'vs same point last month',
          to: { name: 'BookedEvents' },
        },
      ]
    },
    attentionItems() {
      const a = (this.stats && this.stats.attention) || {}
      return [
        {
          key: 'awaiting_quote',
          label: 'Leads awaiting a quote',
          hint: 'New in the last 14 days, no quote sent',
          icon: 'mdi-email-alert-outline',
          color: '#E8A33D',
          count: a.leads_awaiting_quote || 0,
          to: { name: 'Leads' },
        },
        {
          key: 'unbooked',
          label: 'Unbooked upcoming events',
          hint: 'No payment or supplier booking yet',
          icon: 'mdi-calendar-alert-outline',
          color: '#E57373',
          count: a.unbooked_upcoming || 0,
          to: { name: 'UnbookedEvents' },
        },
        {
          key: 'close_offs',
          label: 'Close offs in the next 2 weeks',
          hint: 'Booked events starting soon',
          icon: 'mdi-calendar-clock-outline',
          color: '#3988AA',
          count: a.close_offs_2_weeks || 0,
          to: { name: 'CloseOffEvents', query: { weeks: 2 } },
        },
        {
          key: 'upcoming',
          label: 'Booked events in the next 30 days',
          hint: 'Check suppliers and itineraries',
          icon: 'mdi-calendar-check-outline',
          color: '#3988AA',
          count: (this.stats && this.stats.upcoming_30_days) || 0,
          to: { name: 'BookedEvents' },
        },
      ]
    },
  },
  created() {
    this.setPageName('Dashboard')
  },
  async mounted() {
    // Keep the greeting right if the dashboard is left open across morning/afternoon/evening
    this._clock = setInterval(() => { this.now = new Date() }, 60000)
    await Promise.all([this.getStats(), this.getEvents(), this.getLeads()])
  },
  beforeDestroy() {
    clearInterval(this._clock)
  },
  methods: {
    ...mapActions({
      setPageName: 'pageNameStore/setPageNameAction'
    }),
    formatNumber(value, decimals = 0) {
      return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: decimals })
    },
    // Month-to-date vs the same span of last month
    trend(current, previous) {
      current = Number(current || 0)
      previous = Number(previous || 0)
      if (!previous) return current ? { direction: 'up', text: 'New this month' } : null
      const pct = Math.round(((current - previous) / previous) * 100)
      if (pct === 0) return { direction: 'flat', text: 'Level with last month' }
      return { direction: pct > 0 ? 'up' : 'down', text: `${Math.abs(pct)}%` }
    },
    daysUntil(date) {
      const days = this.$date(date).startOf('day').diff(this.$date(this.now).startOf('day'), 'day')
      if (days <= 0) return 'Today'
      if (days === 1) return 'Tomorrow'
      return `In ${days} days`
    },
    async getStats() {
      this.stats_loading = true
      if (this.get_selected_company.id) {
        try {
          const response = await this.$axios.get(`admin/dashboard/get_stats/${this.get_selected_company.id}`)
          if (response.data.response) {
            this.stats = response.data.data
          }
        } catch (e) {
          this.stats = null
        }
      }
      this.stats_loading = false
    },
    async getEvents() {
      this.events_loading = true
      if (this.get_selected_company.id) {
        const response = await this.$axios.get(
          `admin/events/get_events_paginated/${this.get_selected_company.id}`,
          { page: 1, per_page: UPCOMING_EVENTS_LIMIT + 4, event_time: 'upcoming' }
        )
        const result = response.data
        if (result.response) {
          this.events = result.data
            .filter(e => e.lead && e.lead.start_date && e.event_status !== 'Event Cancelled' && !e.is_cancelled)
            .slice(0, UPCOMING_EVENTS_LIMIT)
        }
      }
      this.events_loading = false
    },
    async getLeads() {
      this.leads_loading = true
      if (this.get_selected_company.id) {
        const response = await this.$axios.get(`admin/leads/get_leads_per_day/${this.get_selected_company.id}`)
        const result = response.data
        if (result.response) {
          this.buildLeadsChart(result.data)
        }
      }
      this.leads_loading = false
    },
    buildLeadsChart(perDay) {
      const labels = Object.keys(perDay).map(d => this.$date(d).format('DD MMM'))
      const counts = Object.values(perDay)
      this.leads_chart_options = {
        ...this.leads_chart_options,
        xaxis: { ...this.leads_chart_options.xaxis, categories: labels },
      }
      this.leads_chart_series = [{ name: 'Leads', data: counts }]
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-container {
  font-family: Manrope, sans-serif;
}

/* Greeting */
.dash-greeting-title {
  font-family: Manrope;
  font-size: 26px;
  font-weight: 800;
  color: #1F2A30;
  line-height: 1.25;
  margin: 0;
}

.dash-greeting-sub {
  font-family: Manrope;
  font-size: 14px;
  color: #7C7C7C;
  margin-top: 4px;
}

/* Cards */
.dash-card {
  border-radius: 10px !important;
  overflow: hidden;
  border: 1px solid #E9E9E9 !important;
  box-shadow: 5px 5px 13px 0px rgba(240, 240, 240, 0.90),
    -5px -5px 10px 0px rgba(255, 255, 255, 0.90) !important;
}

.dash-card-header {
  background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.dash-card-title {
  font-family: Manrope;
  font-size: 13px;
  font-weight: 700;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

:deep(.dash-header-btn span) {
  font-family: Manrope;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  opacity: 0.9;
  box-shadow: none !important;
}

.dash-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  color: #AAAAAA;
  font-family: Manrope;
  font-size: 13px;
  font-weight: 500;
}

/* Stat tiles */
.stat-tile-link {
  text-decoration: none;
  display: block;
  height: 100%;
}

.stat-tile {
  height: 100%;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: #B9D8E6 !important;
    transform: translateY(-1px);
  }
}

.stat-label {
  font-family: Manrope;
  font-size: 12px;
  font-weight: 700;
  color: #7C7C7C;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #E6F1F6;
}

.stat-value {
  font-family: Manrope;
  font-size: 30px;
  font-weight: 800;
  color: #1F2A30;
  line-height: 1.2;

  &--loading {
    color: #D0D0D0;
  }
}

.stat-trend {
  display: inline-flex;
  align-items: center;
  font-family: Manrope;
  font-size: 12px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 8px;

  &--up {
    background: #E7F5EA;
    color: #2E7D32;
  }

  &--down {
    background: #FDECEC;
    color: #C62828;
  }

  &--flat {
    background: #F0F0F0;
    color: #7C7C7C;
  }
}

.stat-trend-icon {
  color: inherit !important;
  margin-right: 2px;
}

.stat-sub {
  font-family: Manrope;
  font-size: 12px;
  color: #9E9E9E;
}

/* Needs attention */
.attention-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid #F2F2F2;
  text-decoration: none;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #F5FAFD;
  }
}

.attention-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #F7F9FB;
  flex-shrink: 0;

  &--clear {
    background: #F1F8F2;
  }
}

.attention-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.attention-label {
  font-family: Manrope;
  font-size: 13px;
  font-weight: 700;
  color: #1F2A30;
}

.attention-hint {
  font-family: Manrope;
  font-size: 11px;
  color: #9E9E9E;
}

.attention-count {
  font-family: Manrope;
  font-size: 18px;
  font-weight: 800;
  color: #1F2A30;
  min-width: 28px;
  text-align: right;

  &--clear {
    display: inline-flex;
    justify-content: flex-end;
  }
}

/* Upcoming events */
.events-list {
  max-height: 520px;
  overflow-y: auto;
}

.event-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #F2F2F2;
  gap: 16px;

  &--alt {
    background-color: #F7F9FB;
  }

  &:last-child {
    border-bottom: none;
  }
}

.event-when {
  width: 48px;
  flex-shrink: 0;
  text-align: center;
  border-radius: 8px;
  background: #E6F1F6;
  padding: 4px 0;
}

.event-when-day {
  font-family: Manrope;
  font-size: 18px;
  font-weight: 800;
  color: #3988AA;
  line-height: 1.1;
}

.event-when-month {
  font-family: Manrope;
  font-size: 11px;
  font-weight: 700;
  color: #3988AA;
  text-transform: uppercase;
}

.event-row-main {
  flex: 1;
  min-width: 0;
}

.event-name {
  font-family: Manrope;
  font-size: 13px;
  font-weight: 700;
  color: #3988AA;
  text-decoration: underline;

  &:hover {
    color: #3988AA;
    text-decoration: underline;
  }
}

.event-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.event-meta-chip {
  display: inline-flex;
  align-items: center;
  font-family: Manrope;
  font-size: 11px;
  color: #7C7C7C;
  font-weight: 500;
}

.event-row-right {
  text-align: right;
  flex-shrink: 0;
}

.event-countdown {
  font-family: Manrope;
  font-size: 12px;
  font-weight: 700;
  color: #1F2A30;
}

.event-code {
  font-family: Manrope;
  font-size: 11px;
  font-weight: 700;
  color: #BBBBBB;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

::-webkit-scrollbar {
  width: 5px;
  background: transparent;
}

::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: #E0E0E0;
}
</style>
